import {
  first_present,
  format_organization_type,
  to_plain_text,
} from "./organization_profile_core";

export {
  append_active_organization_form_data,
  append_active_organization_id,
  format_organization_type,
  get_active_organization_id,
  get_current_organization_permissions,
  get_organization_banner_gradient,
  get_organization_branding,
  get_organization_branding_storage_keys,
  get_retrieve_users_params,
  is_admin_user_role,
  is_active_organization_public,
  is_enabled_feature,
  is_organization_admin_role,
  parse_json_or_null,
  remove_organization_branding,
  save_organization_branding,
  set_current_organization_permissions,
  to_plain_text,
} from "./organization_profile_core";

export function is_persistent_logo_url(value) {
  const logo_url = String(value || "").trim();
  return !!logo_url &&
    !["null", "undefined"].includes(logo_url.toLowerCase()) &&
    !logo_url.startsWith("blob:");
}

export function first_persistent_logo_url(...urls) {
  return urls.find((url) => is_persistent_logo_url(url)) || "";
}

function get_profile_files(company = {}) {
  return [
    ...(company.profile_files || []),
    ...(company.base_profile?.profile_files || []),
  ];
}

function get_profile_file_url(file = {}) {
  return first_present(
    file?.file?.file_url,
    file?.file?.url,
    file?.file_url,
    file?.url
  );
}

function is_logo_file(file = {}) {
  return ["profile_picture", "logo"].includes(
    file.profile_file_type || file.file?.profile_file_type
  );
}

export function get_profile_logo(company = {}, details = {}) {
  const safe_company = company || {};
  const profile_files = get_profile_files(safe_company);
  const logo_file = profile_files.find(is_logo_file) || profile_files[0];

  return first_present(
    safe_company.logo,
    safe_company.logo_url,
    safe_company.company_logo,
    safe_company.organization_logo,
    get_profile_file_url(logo_file),
    details?.organization_logo
  ) || null;
}

export function get_profile_cover(company = {}, details = {}) {
  const safe_company = company || {};
  const cover_file = get_profile_files(safe_company).find((file) =>
    file?.profile_file_type === "cover_picture" ||
    file?.profile_file_type === "cover" ||
    file?.file?.profile_file_type === "cover_picture" ||
    file?.file?.profile_file_type === "cover"
  );

  return (
    first_present(
      safe_company?.cover_picture,
      safe_company?.cover,
      cover_file?.file?.file_url,
      cover_file?.file?.url,
      cover_file?.file_url,
      cover_file?.url,
      details?.organization_cover
    ) || null
  );
}

export function get_active_profile_id(profile = {}) {
  return profile.company?.base_profile?.id || profile.id || null;
}

function get_organization_type(company = {}, details = {}) {
  const organization_type = first_present(
    company?.type_of_organization,
    company?.organization_type,
    company?.type,
    details?.type_of_organization
  );

  if (!organization_type) return "Organisation";

  if (typeof organization_type === "object") {
    return first_present(
      organization_type?.type_of_organization,
      organization_type?.name,
      organization_type?.label,
      "Organisation"
    );
  }

  if (Number.isFinite(Number(organization_type))) {
    return "Organisation";
  }

  return format_organization_type(organization_type);
}

function get_organization_colors(company = {}, details = {}, branding = {}) {
  const safe_company = company || {};
  const safe_details = details || {};
  const safe_branding = branding || {};

  return {
    primary_color: first_present(
      safe_branding?.primary_color,
      safe_company?.primary_color,
      safe_details?.organization_primary_color
    ),

    secondary_color: first_present(
      safe_branding?.secondary_color,
      safe_company?.secondary_color,
      safe_details?.organization_secondary_color
    ),

    tertiary_color: first_present(
      safe_branding?.tertiary_color,
      safe_company?.tertiary_color,
      safe_details?.organization_tertiary_color
    ),
  };
}
function get_organization_contact(company = {}, details = {}) {
  const safe_company = company || {};
  const safe_details = details || {};

  return {
    location: first_present(
      safe_company?.location,
      safe_company?.organization_location,
      safe_company?.address,
      safe_company?.street_address,
      safe_details?.organization_location
    ),

    email: first_present(
      safe_company?.email,
      safe_company?.organization_email,
      safe_company?.company_email,
      safe_details?.organization_email
    ),

    tell_number: first_present(
      safe_company?.tell_number,
      safe_company?.organization_tell_number,
      safe_company?.telephone,
      safe_company?.phone_number,
      safe_company?.company_telephone,
      safe_details?.organization_tell_number
    ),
  };
}

function get_organization_name(company = {}, base_profile = {}, details = {}) {
  return first_present(
    company.company_name,
    company.organization_name,
    company.name,
    base_profile.profile_name,
    details?.organization_name,
    details?.company_name,
    "Your Organisation"
  );
}

function get_organization_description(company = {}, details = {}) {
  return to_plain_text(
    first_present(
      company.description,
      company.organization_description,
      company.company_description,
      company.about,
      details?.organization_description
    )
  );
}

function get_organization_identity(company = {}, base_profile = {}, details = {}) {
  return {
    id: base_profile.id || company.id || null,
    company_id: company.id || null,
    company_name: get_organization_name(company, base_profile, details),
    type_of_organization: get_organization_type(company, details),
    description: get_organization_description(company, details),
    is_private: company.is_private === true || details?.is_private === true,
  };
}

function get_organization_media(company = {}, details = {}, branding = null) {
  return {
    logo: first_persistent_logo_url(
      get_profile_logo(company, details),
      branding?.logo_preview
    ),
    cover: get_profile_cover(company, details),
  };
}

function get_profile_branding_ids(organization = {}, company = {}) {
  return {
    organization_id: first_present(organization.company_id, company?.id) || null,
    profile_id: first_present(organization.id, company?.base_profile?.id) || null,
  };
}

function has_visible_branding(branding = {}) {
  return [
    branding.logo_preview,
    branding.primary_color,
    branding.secondary_color,
    branding.tertiary_color,
  ].some(Boolean);
}

function value_or_empty(value) {
  return value || "";
}

function build_profile_branding(organization = {}, ids = {}) {
  return {
    ...ids,
    logo_preview: value_or_empty(organization.logo),
    cover_preview: value_or_empty(organization.cover),
    primary_color: value_or_empty(organization.primary_color),
    secondary_color: value_or_empty(organization.secondary_color),
    tertiary_color: value_or_empty(organization.tertiary_color),
  };
}

export function normalize_organization_profile(
  company = {},
  details = {},
  branding = null
) {
  const safe_company = company || {};
  const base_profile = safe_company.base_profile || {};

  return {
    ...get_organization_identity(safe_company, base_profile, details),
    ...get_organization_colors(safe_company, details, branding),
    ...get_organization_media(safe_company, details, branding),
    ...get_organization_contact(safe_company, details),
  };
}

export function get_organization_profile_branding(company = {}, details = {}) {
  const organization = normalize_organization_profile(company, details);
  const branding = build_profile_branding(
    organization,
    get_profile_branding_ids(organization, company)
  );

  return has_visible_branding(branding) ? branding : null;
}
